import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output, PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { SettingsService } from '@app/settings.service';
import { STREAMING_SERVICE_URI } from '@modules/video-player/video-player.module';
import { DialogService } from 'primeng/dynamicdialog';
import { of, Subject, timer } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import videojs from 'video.js';
import { VideoModalComponent } from './video-modal/video-modal.component';
import { NgxMaskModule, NgxMaskPipe } from 'ngx-mask';
import { UtilService } from '@helpers/util.service';
import { VideoReviewType } from '@modules/real-property-data/services';

export type GradientClass = 'blue' | 'orange' | 'red' | 'green' | 'purple' | 'yellow';

@Component({
  selector: 'app-video-review',
  templateUrl: './video-review.component.html',
  standalone: true,
  imports: [CommonModule, NgxMaskModule],
  styleUrls: ['./video-review.component.scss'],
  providers: [NgxMaskPipe],
})
export class VideoReviewComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  player: videojs.Player;
  videoThumbnail = 'assets/images/video-loading.png';
  videoThumbnailBlob: string;
  isMobile: boolean;
  isTablet: boolean;
  isPlayerReady = false;
  isModalOpen: boolean;
  videoId: string;
  showPoster: boolean = true;

  destroy$ = new Subject();
  autoPlaySubject$: Subject<boolean> = new Subject<boolean>();
  stopSubject$: Subject<any> = new Subject<any>();
  isBrowser: boolean;

  @Input() videoInfo: VideoReviewType;
  @Input() height: string = '390px';
  @Input() width: string = '247px';
  @Input() saleText: string;
  @Input() gradientClass: GradientClass;
  @Input() borderRadius: string = '12px';
  @Input() disabled: boolean;
  @Input() isActive: boolean;

  @Output() clickInfoEvent: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('videoPlayer', { static: true }) videoPlayer: ElementRef;

  constructor(
    @Inject(STREAMING_SERVICE_URI) private streamingServiceURI: string,
    private _cdr: ChangeDetectorRef,
    private _dialogService: DialogService,
    private _settingsService: SettingsService,
    private renderer: Renderer2,
    private _utilService: UtilService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.videoThumbnail = this.getVideoUrl().replace('/master.m3u8', '/thumbnail.png');
    if (!this.disabled) {
      this.initPlayer();
    }
    this.createIdForVideo();

    this._settingsService.isMobile().pipe(takeUntil(this.destroy$)).subscribe((res) => this.isMobile = res);
    this._settingsService.isTablet().pipe(takeUntil(this.destroy$)).subscribe((res) => this.isTablet = res);


    this.autoPlaySubject$
      .pipe(
        filter(() => !this.isMobile && !this.isTablet),
        takeUntil(this.destroy$),
        switchMap((shouldStart) => {
          if (shouldStart) {
            return timer(2000).pipe(map(() => true));
          } else {
            return of(false);
          }
        }),
      )
      .subscribe((value) => {
        this.toggleVideo(value);
      });
  }

  getPoster() {
    return `url(${this.videoThumbnail})`;
  }

  getVideoHeight(): string {
    const height = +this.height.substring(0, 3);
    return this.isActive ? `${height - (this.isMobile ? 40 : 60)}px` : this.height;
  }

  clickInfo() {
    this.clickInfoEvent.emit(this.videoInfo.id);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isActive) {
      this.autoPlaySubject$.next(changes.isActive.currentValue);
    }
  }

  ngAfterViewInit() {
    this.renderer.setAttribute(this.videoPlayer.nativeElement, 'poster', this.videoThumbnail);
  }

  ngOnDestroy() {
    this.player = null;
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public getVideoUrl(): string {
    if (!this.streamingServiceURI || !this.streamingServiceURI.startsWith('http')) {
      console.error('Error: Incorrect base Url:', this.streamingServiceURI);
      return this.videoInfo.src;
    }

    if (this.videoInfo.src.startsWith('http')) {
      return this.videoInfo.src;
    }

    try {
      return new URL(this.videoInfo.src, this.streamingServiceURI).href;
    } catch (error) {
      console.error('Error build url:', error);
      return this.videoInfo.src;
    }
  }

  initPlayer() {
    if (!this.videoPlayer || !this.isBrowser) return;
    this.player = videojs(this.videoPlayer.nativeElement, {
      autoplay: false,
      controls: false,
      bigPlayButton: false,
      controlBar: {
        playToggle: true,
        currentTimeDisplay: false,
        timeDivider: false,
        durationDisplay: false,
        remainingTimeDisplay: false,
        liveDisplay: false,
        progressControl: true,
        volumePanel: false,
        fullscreenToggle: true,
      },
    });

    this.videoInfo = { ...this.videoInfo, src: this.getVideoUrl() };

    this.player.textTracks();
    this.player.src({
      src: this.videoInfo.src,
      type: 'application/x-mpegURL',
    });

    this.player.on('ready', () => {
      this.isPlayerReady = true;
    });

    this.hideUnnecessaryElements();
    this.player.on('click', (e) => {
      this.handleVideo(e);
    });
  }

  handleVideo(event: Event) {
    event.stopPropagation();
    event.preventDefault();

    document.querySelectorAll('video').forEach((videoElem) => {
      const player = videojs(videoElem);
      if (player != this.player) {
        player.pause();
      }
    });
    this.toggleVideo(this.player.paused());
    this._cdr.detectChanges();
  }

  hideUnnecessaryElements() {
    this.player.loadingSpinner.hide();
    this._cdr.detectChanges();
  }

  public changeSound(event: MouseEvent): void {
    event.stopPropagation();
    this.player.muted() ? this.player.muted(false) : this.player.muted(true);
  }

  toggleVideo(value: boolean) {
    if (value) {
      this.player.play();
      this.showPoster = false;
    } else {
      this.player.pause();
    }
  }

  createIdForVideo() {
    this.videoId = `video-preview-${this.videoInfo.id}`;
  }

  openFullScreen() {
    this.player.requestFullscreen();
    const closeButton = document.createElement('button');
    const img = document.createElement('img');
    img.src = './assets/images/new-design-icons/close-white.svg';
    closeButton.appendChild(img);
    closeButton.classList.add('full-screen-close');
    closeButton.style.display = 'none';
    closeButton.addEventListener('click', this.closeFullScreen.bind(this));

    document.querySelector(`#${this.player.id()}`).appendChild(closeButton);

    this.player.on('fullscreenchange', () => {
      if (this.player.isFullscreen()) {
        closeButton.style.display = 'flex';
      } else {
        this.player.pause();
        closeButton.style.display = 'none';
      }
    });
  }

  closeFullScreen() {
    this.player.exitFullscreen();
  }

  openVideo() {
    this.isModalOpen = true;
    const modal = this._dialogService.open(VideoModalComponent, {
      data: {
        videoInfo: this.videoInfo,
        videoThumbnail: this.videoThumbnail,
        player: this.player,
      },
      styleClass: 'modal-video',
      showHeader: false,
      baseZIndex: 10000,
      dismissableMask: true,
      modal: true,
    });

    modal.onClose.subscribe(() => {
      this.isModalOpen = false;
    });
  }
}
